import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { choiceOfName, type ThinkingChoice } from '../data/thinking-levels';

import { userScopedLocalStorage } from './user-scoped-storage';

interface ThinkingLevelState {
  /**
   * The user's last thinking choice (iq-ai-cio `docs/features/thinking-levels.md`, "The stored
   * choice"): the level's id and rank, or null while none was chosen. Each new chat starts from
   * it, and a chat kept before levels were per chat reads it until its first write
   * (`conversations.store.ts::chatChoice`). Every write of a chat's level records it here too.
   */
  choice: ThinkingChoice | null;
  setChoice: (choice: ThinkingChoice) => void;
}

type Stored = Pick<ThinkingLevelState, 'choice'>;

function isChoice(value: unknown): value is ThinkingChoice {
  if (typeof value !== 'object' || value === null) return false;
  const { id, rank } = value as Record<string, unknown>;
  return typeof id === 'string' && id !== '' && typeof rank === 'number' && Number.isFinite(rank);
}

/**
 * The store before the per-family levels (version 0) held an old name, `{level, agentic}`: it
 * reads as the step the name names, `fast` as Low and `deep` as Extra high. Version 1 held the
 * choice and the Agentic switch. The switch is the chat's now, off in every new chat, so a
 * stored one is dropped.
 */
function migrate(persisted: unknown, version: number): Stored {
  const stored = (persisted ?? {}) as { level?: unknown; choice?: unknown };
  if (version === 0) {
    const name = typeof stored.level === 'string' ? stored.level.trim().toLowerCase() : '';
    return { choice: name ? choiceOfName(name) : null };
  }
  const { choice } = stored;
  return { choice: isChoice(choice) ? { id: choice.id, rank: choice.rank } : null };
}

/**
 * The user's last thinking choice on this browser, the seed of each new chat's level. Kept
 * under a key scoped to the user (`investbanq-thinking-level:<userId>`), so a choice one advisor
 * made never seeds another's chats on a shared browser. A preference, not chat data, so the key
 * is outside the `investbanq-ai-cio` prefix that logout wipes, and a user's choice outlasts
 * their own logout.
 */
export const useThinkingLevelStore = create<ThinkingLevelState>()(
  persist(
    (set) => ({
      choice: null,
      setChoice: (choice) => {
        set({ choice: { id: choice.id, rank: choice.rank } });
      },
    }),
    {
      name: 'investbanq-thinking-level',
      version: 2,
      storage: createJSONStorage(() => userScopedLocalStorage),
      partialize: (state): Stored => ({ choice: state.choice }),
      migrate,
      // What a user stored, read as it is; nothing stored (another user's bucket) is no choice.
      merge: (persisted, current) => ({ ...current, ...migrate(persisted, 2) }),
    },
  ),
);
